import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductionOrders.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { downloadCsv } from '../../utils/exportCsv';
import { isAdmin as checkAdmin } from '../../utils/roleUtils';
import { formatAppDate } from '../../utils/dateUtils';

const ProductionOrdersList = forwardRef(({ onSelectOrder, selectedOrderId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const { user } = useAuth();
  const isAdmin = checkAdmin(user);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchOrders
  }));

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, priorityFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/production/orders?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        const ordersList = data.orders || data;
        setOrders(Array.isArray(ordersList) ? ordersList : []);
      } else {
        setError(t('ordersList.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching production orders:', error);
      setError(t('ordersList.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = (order.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.special_instructions?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'planned' && order.status?.toUpperCase() === 'PLANNED') ||
                         (statusFilter === 'released' && order.status?.toUpperCase() === 'RELEASED') ||
                         (statusFilter === 'in_progress' && order.status?.toUpperCase() === 'IN_PROGRESS') ||
                         (statusFilter === 'completed' && order.status?.toUpperCase() === 'COMPLETED') ||
                         (statusFilter === 'cancelled' && order.status?.toUpperCase() === 'CANCELLED') ||
                         (statusFilter === 'on_hold' && order.status?.toUpperCase() === 'ON_HOLD');

    const matchesPriority = priorityFilter === 'all' ||
                           (priorityFilter === 'low' && order.priority?.toUpperCase() === 'LOW') ||
                           (priorityFilter === 'normal' && order.priority?.toUpperCase() === 'NORMAL') ||
                           (priorityFilter === 'high' && order.priority?.toUpperCase() === 'HIGH') ||
                           (priorityFilter === 'urgent' && order.priority?.toUpperCase() === 'URGENT');

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getPriorityBadge = (priority) => {
    const priorityUpper = priority?.toUpperCase();
    let className = 'priority-badge ';

    switch (priorityUpper) {
      case 'LOW':
        className += 'priority-low';
        break;
      case 'NORMAL':
        className += 'priority-normal';
        break;
      case 'HIGH':
        className += 'priority-high';
        break;
      case 'URGENT':
        className += 'priority-urgent';
        break;
      default:
        className += 'priority-normal';
    }

    return <span className={className}>{priority || t('priority.normal')}</span>;
  };

  const getStatusBadge = (status) => {
    const statusUpper = status?.toUpperCase();
    let className = 'status-badge ';

    switch (statusUpper) {
      case 'PLANNED':
        className += 'status-pending';
        break;
      case 'RELEASED':
        className += 'status-confirmed';
        break;
      case 'IN_PROGRESS':
        className += 'status-in-progress';
        break;
      case 'COMPLETED':
        className += 'status-completed';
        break;
      case 'ON_HOLD':
        className += 'status-on-hold';
        break;
      case 'CANCELLED':
        className += 'status-cancelled';
        break;
      default:
        className += 'status-inactive';
    }

    const displayText = status === 'IN_PROGRESS' ? t('status.inProgress') :
                       status === 'ON_HOLD' ? t('status.onHold') :
                       status ? t(`status.${status.toLowerCase()}`) : t('status.planned');
    return <span className={className}>{displayText}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('ordersList.defaultValues.notSet');
    const date = new Date(dateString);
    const today = new Date();
    const isOverdue = date < today;

    const formatted = formatAppDate(date);

    return (
      <span className={`due-date ${isOverdue ? 'overdue' : ''}`}>
        {formatted}
      </span>
    );
  };

  const renderProgress = (progress) => {
    const percentage = Math.max(0, Math.min(100, progress || 0));

    return (
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${percentage}%` }}
        />
        <span className="progress-text">{percentage}%</span>
      </div>
    );
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && orders.length === 0) {
    return <div className="list-container"><div className="loading">{t('ordersList.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('ordersList.title')}</h2>
        <div className="header-actions">
          {isAdmin && (
            <button
              className="btn-secondary"
              onClick={() => downloadCsv('production-orders', { status: statusFilter })}
              title={t('common:actions.export', 'Export CSV')}
            >
              CSV
            </button>
          )}
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('ordersList.newOrder')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('ordersList.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('ordersList.filters.allStatus')}</option>
            <option value="planned">{t('status.planned')}</option>
            <option value="released">{t('status.released')}</option>
            <option value="in_progress">{t('status.inProgress')}</option>
            <option value="on_hold">{t('status.onHold')}</option>
            <option value="completed">{t('status.completed')}</option>
            <option value="cancelled">{t('status.cancelled')}</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('ordersList.filters.allPriority')}</option>
            <option value="low">{t('priority.low')}</option>
            <option value="normal">{t('priority.normal')}</option>
            <option value="high">{t('priority.high')}</option>
            <option value="urgent">{t('priority.urgent')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '13%' }}>{t('ordersList.headers.poNumber')}</th>
              <th style={{ width: '18%' }}>{t('ordersList.headers.customer')}</th>
              <th style={{ width: '24%' }}>{t('ordersList.headers.description')}</th>
              <th style={{ width: '10%' }}>{t('ordersList.headers.priority')}</th>
              <th style={{ width: '13%' }}>{t('ordersList.headers.dueDate')}</th>
              <th style={{ width: '12%' }}>{t('ordersList.headers.status')}</th>
              <th style={{ width: '10%' }}>{t('ordersList.headers.progress')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm ? t('ordersList.noData.noResults') : t('ordersList.noData.noOrders')}
                </td>
              </tr>
            ) : (
              filteredOrders.map(order => (
                <tr
                  key={order.production_order_id || order.po_number}
                  onClick={() => onSelectOrder(order.po_number || order.production_order_id)}
                  className={selectedOrderId === (order.po_number || order.production_order_id) ? 'selected' : ''}
                >
                  <td>
                    <span className="job-number">{order.po_number || 'N/A'}</span>
                  </td>
                  <td>
                    <div className="customer-name">
                      <strong>{order.customer_name || t('ordersList.defaultValues.unknownCustomer')}</strong>
                    </div>
                    {order.product_type && (
                      <div className="product-name">{order.product_type}</div>
                    )}
                  </td>
                  <td className="description">
                    <div className="order-notes" title={order.notes || undefined}>{order.notes || t('ordersList.defaultValues.noDescription')}</div>
                    {order.special_instructions && (
                      <div className="special-instructions" title={order.special_instructions}>
                        {order.special_instructions.length > 50
                          ? order.special_instructions.substring(0, 50) + '...'
                          : order.special_instructions}
                      </div>
                    )}
                  </td>
                  <td>
                    {getPriorityBadge(order.priority)}
                  </td>
                  <td>
                    {formatDate(order.planned_due_date || order.due_date)}
                  </td>
                  <td>
                    {getStatusBadge(order.status)}
                  </td>
                  <td>
                    {renderProgress(order.progress)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('ordersList.footer.recordCount', { filtered: filteredOrders.length, total: orders.length })}
        </div>
      </div>
    </div>
  );
});

ProductionOrdersList.displayName = 'ProductionOrdersList';

export default ProductionOrdersList;